import { toastr } from 'react-redux-toastr';
import { makeApiCall } from '../../../../../services/apiService/apiService';
import { downloadCSV, createCSV } from '../../../../../services/helpers';
import {
  claimRoleTypes,
  getClaimRole,
  getColumnsForRole
} from '../claimsDashboardHelper';
import {
  getClientVisibleStatus,
  CLIENT_STATUS_GROUPINGS
} from '../../claimsUtils';
import {
  buildClaimFilters,
  buildClaimDownloadParams
} from './claimFilterBuilder';

export const claimsDashboardActions = {
  SET_CLAIMS_DATA: 'SET_CLAIMS_DATA',
  UPDATE_PAGE_NUMBER: 'UPDATE_CLAIMS_PAGE_NUMBER',
  UPDATE_FILTER: 'UPDATE_CLAIMS_FILTER',
  UPDATE_SEARCH: 'UPDATE_CLAIMS_SEARCH'
};

export const setClaimsDataAction = payload => ({
  type: claimsDashboardActions.SET_CLAIMS_DATA,
  payload
});

export const updateClaimsPageNumber = payload => ({
  type: claimsDashboardActions.UPDATE_PAGE_NUMBER,
  payload
});

export const updateClaimsFilter = (key, payload) => ({
  type: claimsDashboardActions.UPDATE_FILTER,
  key,
  payload
});

export const updateClaimsSearch = payload => ({
  type: claimsDashboardActions.UPDATE_SEARCH,
  payload
});

const getClaimsDownloadReplaceParams = (claimsDashboardState, isClientUser) => {
  return buildClaimDownloadParams(claimsDashboardState, isClientUser);
};

export const getClaimsList = () => (dispatch, getState) => {
  const state = getState();
  const { claimsDashboardState } = state;
  const { userProfile } = state;
  const role = getClaimRole(userProfile);
  const isClientUser =
    role === claimRoleTypes.CLIENT_USER || role === claimRoleTypes.RP_USER;

  dispatch(
    setClaimsDataAction({
      isLoading: true,
      isSuccess: false
    })
  );

  const { claimFilterDTO, hasFilter } = buildClaimFilters(
    claimsDashboardState,
    isClientUser
  );

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'getClaimsList',
    method: 'POST',
    replaceParamsArr: [
      { key: 'page', val: claimsDashboardState.pageNumber },
      { key: 'size', val: claimsDashboardState.pageSize }
    ].filter(
      item => item.val !== null && item.val !== undefined && item.val !== ''
    ),
    data: hasFilter ? claimFilterDTO : null,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        const payload = res.payload || {};
        dispatch(
          setClaimsDataAction({
            isLoading: false,
            isSuccess: true,
            claimsData: payload.content || [],
            totalElements: payload.totalElements || 0,
            showPagination: true
          })
        );
      } else {
        dispatch(
          setClaimsDataAction({
            isLoading: false,
            isSuccess: false,
            claimsData: [],
            totalElements: 0
          })
        );
      }
    },
    failureCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false,
          isSuccess: false,
          claimsData: [],
          totalElements: 0
        })
      );
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not fetch claims'
      );
    },
    catchCb: () => {
      dispatch(
        setClaimsDataAction({
          isLoading: false,
          isSuccess: false,
          claimsData: [],
          totalElements: 0
        })
      );
      toastr.error('Something went wrong!', 'Could not fetch claims');
    }
  };

  return makeApiCall(requestObj);
};

export const searchClaimsByCn = () => (dispatch, getState) => {
  const { claimsDashboardState } = getState();
  const cnote = (claimsDashboardState.search || '').trim();

  if (!cnote) {
    return dispatch(getClaimsList());
  }

  dispatch(
    setClaimsDataAction({
      isLoading: true,
      isSuccess: false
    })
  );

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'searchClaimsByCn',
    method: 'GET',
    replaceParamsArr: [{ key: 'cn', val: cnote }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        const payload = res.payload || [];
        dispatch(
          setClaimsDataAction({
            isLoading: false,
            isSuccess: true,
            claimsData: payload,
            totalElements: payload.length || 0,
            pageNumber: 0,
            showPagination: false
          })
        );
      } else {
        dispatch(
          setClaimsDataAction({
            isLoading: false,
            isSuccess: false,
            claimsData: [],
            totalElements: 0,
            showPagination: false
          })
        );
      }
    },
    failureCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false,
          isSuccess: false,
          claimsData: [],
          totalElements: 0,
          showPagination: false
        })
      );
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not search claims'
      );
    },
    catchCb: () => {
      dispatch(
        setClaimsDataAction({
          isLoading: false,
          isSuccess: false,
          claimsData: [],
          totalElements: 0,
          showPagination: false
        })
      );
      toastr.error('Something went wrong!', 'Could not search claims');
    }
  };

  return makeApiCall(requestObj);
};

function refreshClaimsData(dispatch, getState) {
  const { claimsDashboardState } = getState();

  if ((claimsDashboardState.search || '').trim()) {
    return dispatch(searchClaimsByCn());
  }

  return dispatch(getClaimsList());
}

export const revokeClaim = (claimId, payload = {}, callbacks = {}) => (
  dispatch,
  getState
) => {
  dispatch(
    setClaimsDataAction({
      isLoading: true
    })
  );

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'revokeClaim',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    data: payload,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'Claim revoked successfully');
        refreshClaimsData(dispatch, getState);
        if (callbacks.onSuccess) callbacks.onSuccess(res);
      } else {
        dispatch(
          setClaimsDataAction({
            isLoading: false
          })
        );
        if (callbacks.onFailure) callbacks.onFailure(res);
      }
    },
    failureCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not revoke claim'
      );
      if (callbacks.onFailure) callbacks.onFailure(res);
    },
    catchCb: () => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error('Something went wrong!', 'Could not revoke claim');
      if (callbacks.onFailure) callbacks.onFailure();
    }
  };

  return makeApiCall(requestObj);
};

export const acknowledgeClaim = claimId => (dispatch, getState) => {
  dispatch(
    setClaimsDataAction({
      isLoading: true
    })
  );

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'acknowledgeClaim',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    data: {},
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'Claim acknowledged successfully');
        refreshClaimsData(dispatch, getState);
      } else {
        dispatch(
          setClaimsDataAction({
            isLoading: false
          })
        );
      }
    },
    failureCb: res => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not acknowledge claim'
      );
    },
    catchCb: () => {
      dispatch(
        setClaimsDataAction({
          isLoading: false
        })
      );
      toastr.error('Something went wrong!', 'Could not acknowledge claim');
    }
  };

  return makeApiCall(requestObj);
};

export const generateClaimForm = (claimId, actionType, onSuccess) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'generateClaimForm',
    method: 'GET',
    pathVariables: [{ key: '{id}', val: claimId }],
    successCb: res => {
      if (res.status === 'SUCCESS' && res.payload) {
        const { filename, pdfBase64 } = res.payload;
        if (!pdfBase64) {
          toastr.error('Error', 'PDF content is empty');
          return;
        }

        try {
          const cleanBase64 = pdfBase64.replace(/\s/g, '');
          const byteCharacters = atob(cleanBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i += 1) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const fileURL = window.URL.createObjectURL(blob);

          if (actionType === 'view') {
            if (onSuccess) {
              onSuccess(fileURL);
            } else {
              window.open(fileURL, '_blank');
            }
          } else {
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = filename || `ClaimForm-${claimId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        } catch (e) {
          toastr.error('Error', 'Could not decode claim form PDF');
        }
      } else {
        toastr.error(
          'Error',
          (res && res.errorMessage) || 'Failed to generate claim form'
        );
      }
    },
    failureCb: res => {
      toastr.error(
        'Error',
        (res && res.errorMessage) || 'Failed to generate claim form'
      );
    },
    catchCb: () => {
      toastr.error('Error', 'Failed to generate claim form');
    }
  };

  return makeApiCall(requestObj);
};

export * from './claimDownloadActions';
export * from './claimWorkflowActionCreators';
export * from './claimDataFetchActions';
